import { useCallback, useMemo, useState } from "react";
import { ModalV2, Text, toast, AgGrid } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import {
  useCreateComplianceCaseFromProfileMutation,
  useSendInvitationReminderMutation,
} from "~/graphql/supergraphHooks";
import { BaseRequestFormContent } from "./BaseRequestFormContent";
import { modalSubtitle, modalHeader } from "./styles_vanilla.css";
import { FormValues, RequestType, REQUEST_LABELS, REQUEST_TYPE_TO_CASE_TYPE } from "./types";

export type BaseRequestModalProps = {
  requestType: RequestType;
  profileId?: number;
  profileName?: string;
  profileGuid?: string;
  caseId?: number;
  isResend?: boolean;
  dateSent?: Date;
  requestSentCount?: number;
  emails: string[];
  onClose: () => void;
  api?: AgGrid.GridApi;
};

export const BaseRequestModal = ({
  requestType,
  profileId,
  profileName,
  profileGuid,
  caseId,
  isResend = false,
  dateSent,
  requestSentCount,
  emails,
  onClose,
  api,
}: BaseRequestModalProps) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const labels = REQUEST_LABELS[requestType];
  const caseType = REQUEST_TYPE_TO_CASE_TYPE[requestType];

  const [createComplianceCaseFromProfile, { loading: createCaseLoading }] =
    useCreateComplianceCaseFromProfileMutation({
      fetchPolicy: "network-only",
      onError: (e) => {
        const errorMessage = e.message || labels.errorMessage;
        setSubmitError(errorMessage);
      },
      onCompleted: () => {
        api?.refreshServerSide({ purge: true });
        toast.success(labels.successMessage);
        onClose();
      },
    });

  const [sendInvitationReminder, { loading: sendReminderLoading }] =
    useSendInvitationReminderMutation({
      fetchPolicy: "network-only",
      onError: (e) => {
        const errorMessage = e.message || labels.resendErrorMessage;
        setSubmitError(errorMessage);
      },
      onCompleted: () => {
        api?.refreshServerSide({ purge: true });
        toast.success(labels.resendSuccessMessage);
        onClose();
      },
    });

  const mutationLoading = createCaseLoading || sendReminderLoading;

  const formInitialValues: FormValues = useMemo(
    () => ({
      selectedEmails: emails,
    }),
    [emails],
  );

  const handleHideModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setSubmitError(null);

      if (isResend && caseId) {
        try {
          await sendInvitationReminder({
            variables: {
              input: {
                caseId,
                emailAddresses: values.selectedEmails,
              },
            },
          });
        } catch {
          // Error is handled by onError callback
        }
        return;
      }

      try {
        if (!profileGuid) {
          throw new Error("Profile GUID is required");
        }

        if (!values.selectedEmails || values.selectedEmails.length === 0) {
          throw new Error("Please select at least one email address");
        }

        await createComplianceCaseFromProfile({
          variables: {
            caseInput: {
              profileGuid,
              caseType,
              assignSelf: false,
            },
            emailInviteInput: {
              emailAddresses: values.selectedEmails,
            },
          },
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : labels.errorMessage;
        setSubmitError(errorMessage);
      }
    },
    [
      isResend,
      caseId,
      sendInvitationReminder,
      profileGuid,
      createComplianceCaseFromProfile,
      caseType,
      labels,
    ],
  );

  const validate = useCallback((values: FormValues) => {
    const errors: { selectedEmails?: string } = {};

    if (!values.selectedEmails?.length) {
      errors.selectedEmails = "Please select at least one email address";
    }

    return errors;
  }, []);

  return (
    <ModalV2
      show={true}
      onHide={handleHideModal}
      isCentered={true}
      size="medium"
      backdrop={true}
      title={labels.modalTitle}
      headerClassName={modalHeader}
    >
      <Form<FormValues>
        key={profileId}
        initialValues={formInitialValues}
        onSubmit={handleSubmit}
        validate={validate}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body>
          {profileName && (
            <Text variant="regular-md" className={modalSubtitle}>
              {profileName}
            </Text>
          )}
          <BaseRequestFormContent
            emails={emails}
            onClose={onClose}
            loading={mutationLoading}
            submitError={submitError}
            isResend={isResend}
            dateSent={dateSent}
            requestSentCount={requestSentCount}
            labels={labels}
          />
        </ModalV2.Body>
      </Form>
    </ModalV2>
  );
};

export default BaseRequestModal;
